import { useEffect, useContext, useRef } from 'react';
import * as turf from '@turf/turf';
import _ from 'lodash';
import { MapContext } from '../contexts/MapContext';
import * as normalizers from '../utils/normalizers';
import { mapModes } from '../constants/map';

const useRotate = (map) => {
  const {
    fields: { updateFieldSection },
    mapState: { activeField, mapMode, setContext },
  } = useContext(MapContext);

  const updateFieldSectionRef = useRef();
  updateFieldSectionRef.current = updateFieldSection;

  const { rotationCenter, rotation } = activeField || {};
  const initialBearing = useRef(0);
  const initialRotation = useRef(rotation);
  const currentRotation = useRef(rotation);
  const bearingDiff = useRef(0);

  useEffect(() => {
    currentRotation.current = rotation;
  }, [rotation]);

  useEffect(() => {
    if (mapMode !== mapModes.ROTATE) return undefined;
    const canvas = map.getCanvasContainer();
    const onMove = _.throttle((e) => {
      setContext({ hasRotated: true });
      const coords = e.lngLat;
      canvas.style.cursor = 'grabbing';
      const currentBearing = turf.bearing(rotationCenter, [
        coords.lng,
        coords.lat,
      ]);
      bearingDiff.current = currentBearing - initialBearing.current;
      updateFieldSectionRef.current(activeField.id, {
        rotation: normalizers.asRotationDegrees(
          initialRotation.current + bearingDiff.current,
        ),
      });
    }, 100);
    const onMouseUp = () => {
      updateFieldSectionRef.current(activeField.id, {
        rotation: normalizers.asRotationDegrees(
          initialRotation.current + bearingDiff.current,
        ),
      });
      map.off('mousemove', onMove);
      canvas.style.cursor = 'grab';
    };
    const onMouseDown = (e) => {
      // Prevent the default map drag behavior.
      e.preventDefault();

      canvas.style.cursor = 'grab';
      const coords = e.lngLat;
      initialRotation.current = currentRotation.current;
      initialBearing.current = turf.bearing(rotationCenter, [
        coords.lng,
        coords.lat,
      ]);

      map.on('mousemove', onMove);
      map.once('mouseup', onMouseUp);
    };
    map.on('mousedown', onMouseDown);
    return () => {
      map.off('mousedown', onMouseDown);
      canvas.style.cursor = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapMode]);
};

export default useRotate;
